/**
 * VSScreenOverlay Component
 *
 * Full-screen overlay displayed when both players are ready before combat starts.
 * Shows hero creatures facing off, army previews, and loading progress.
 * Displays for a minimum of 5 seconds before allowing dismiss.
 *
 * Used in:
 * - Battle page (transition from deployment to combat phase)
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import styles from './VSScreenOverlay.module.css';
import { VSCreatureDisplay } from './VSCreatureDisplay';
import { ArmyPreview } from './ArmyPreview';
import { LoadingProgress } from './LoadingProgress';

/** Creature data for display */
interface CreatureData {
  creature: {
    id: string;
    name: string;
    sprite: string;
  };
}

/** Combat initialization phase type */
export type CombatInitPhase = 'idle' | 'initializing' | 'ready' | 'failed';

export interface VSScreenOverlayProps {
  /** Player's deployed creatures */
  playerCreatures: CreatureData[];
  /** Opponent's deployed creatures */
  opponentCreatures: CreatureData[];
  /** Loading progress from 0 to 1 (deprecated - now uses internal timing) */
  loadingProgress: number;
  /** Whether all loading is complete (deprecated - use combatInitPhase instead) */
  isLoadingComplete: boolean;
  /** L4-SEAMLESS-003: Actual combat initialization phase */
  combatInitPhase: CombatInitPhase;
  /** Callback when overlay should be dismissed */
  onComplete: () => void;
  /** Player's display name */
  playerName?: string;
  /** Opponent's display name */
  opponentName?: string;
}

/** Minimum display time in milliseconds
 * L4-SEAMLESS-004: Reduced from 5s to 2s since we now wait for actual combat readiness */
const MIN_DISPLAY_TIME_MS = 2000;

/** Fade out animation duration in milliseconds */
const FADE_OUT_DURATION_MS = 300;

/** Progress update interval in milliseconds */
const PROGRESS_UPDATE_INTERVAL_MS = 50;

/**
 * VSScreenOverlay Component
 *
 * Displays a dramatic VS screen before combat begins, showing both players'
 * hero creatures facing off with loading progress.
 */
export function VSScreenOverlay({
  playerCreatures,
  opponentCreatures,
  loadingProgress: _externalProgress, // Kept for API compatibility but we use internal timing
  isLoadingComplete: _isLoadingComplete, // Deprecated - using combatInitPhase
  combatInitPhase,
  onComplete,
  playerName = 'Player',
  opponentName = 'Opponent',
}: VSScreenOverlayProps) {
  // Track if minimum display time has elapsed
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  // Track if fade out animation is in progress
  const [fadingOut, setFadingOut] = useState(false);
  // Internal progress based on time (0-1)
  const [internalProgress, setInternalProgress] = useState(0);

  // Get random hero creatures (memoized to stay stable during render)
  const playerHero = useMemo(() => {
    if (playerCreatures.length === 0) return null;
    const randomIndex = Math.floor(Math.random() * playerCreatures.length);
    return playerCreatures[randomIndex];
  }, [playerCreatures]);

  const opponentHero = useMemo(() => {
    if (opponentCreatures.length === 0) return null;
    const randomIndex = Math.floor(Math.random() * opponentCreatures.length);
    return opponentCreatures[randomIndex];
  }, [opponentCreatures]);

  // Handle the complete transition with fade out
  const handleComplete = useCallback(() => {
    if (fadingOut) return;

    setFadingOut(true);

    // Wait for fade animation to complete before calling onComplete
    setTimeout(() => {
      onComplete();
    }, FADE_OUT_DURATION_MS);
  }, [fadingOut, onComplete]);

  // Lock body scroll when overlay is mounted
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Track minimum display time
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinTimeElapsed(true);
    }, MIN_DISPLAY_TIME_MS);

    return () => clearTimeout(timer);
  }, []);

  // Animate progress from 0 to 100% over the display time
  useEffect(() => {
    const startTime = Date.now();

    const updateProgress = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / MIN_DISPLAY_TIME_MS, 1);
      setInternalProgress(progress);
    };

    const interval = setInterval(updateProgress, PROGRESS_UPDATE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  // L4-SEAMLESS-004: Trigger completion when combat is ACTUALLY ready
  // Dismiss only when: (1) minimum time elapsed AND (2) combat fully initialized
  useEffect(() => {
    const combatReady = combatInitPhase === 'ready';
    if (minTimeElapsed && combatReady && !fadingOut) {
      console.log('[VSScreenOverlay] Combat ready & min time elapsed - dismissing');
      handleComplete();
    }
  }, [minTimeElapsed, combatInitPhase, fadingOut, handleComplete]);

  return (
    <div className={`${styles.overlay} ${fadingOut ? styles.fadeOut : ''}`}>
      {/* Dark backdrop */}
      <div className={styles.backdrop} />

      {/* Main content container */}
      <div className={styles.content}>
        {/* Hero creatures facing off */}
        <section className={styles.heroSection}>
          {/* Player hero */}
          <div className={styles.playerHero}>
            {playerHero ? (
              <VSCreatureDisplay creature={playerHero.creature} side="left" />
            ) : (
              <div className={styles.emptyHero}>No creatures</div>
            )}
          </div>

          {/* VS Text */}
          <h1 className={styles.vsText}>VS</h1>

          {/* Opponent hero */}
          <div className={styles.opponentHero}>
            {opponentHero ? (
              <VSCreatureDisplay creature={opponentHero.creature} side="right" />
            ) : (
              <div className={styles.emptyHero}>No creatures</div>
            )}
          </div>
        </section>

        {/* Army preview sections */}
        <section className={styles.armyPreviewSection}>
          {/* Player army */}
          <div className={styles.playerArmy}>
            <ArmyPreview
              creatures={playerCreatures}
              label="Your Army"
              side="left"
            />
          </div>

          {/* Opponent army */}
          <div className={styles.opponentArmy}>
            <ArmyPreview
              creatures={opponentCreatures}
              label="Enemy Army"
              side="right"
            />
          </div>
        </section>

        {/* Loading progress */}
        <section className={styles.loadingSection}>
          <LoadingProgress progress={internalProgress} />
        </section>
      </div>
    </div>
  );
}

export default VSScreenOverlay;
